import { Browser } from "@capacitor/browser";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import GoogleColored from "@repo/icons/colored/GoogleColored";
import OnelivColored from "@repo/icons/colored/OnelivColored";
import Adjustments from "@repo/icons/default/Adjustments";
import BlurpIa from "@repo/icons/default/BlurpIa";
import Building from "@repo/icons/default/Building";
import CalendarTime from "@repo/icons/default/CalendarTime";
import EditMessage from "@repo/icons/default/EditMessage";
import Folder from "@repo/icons/default/Folder";
import GlobeCursor from "@repo/icons/default/GlobeCursor";
import GraphList from "@repo/icons/default/GraphList";
import HelpCircle from "@repo/icons/default/HelpCircle";
import LivancePay from "@repo/icons/default/LivancePay";
import Logout from "@repo/icons/default/Logout";
import MoneyBanknoteHandshake from "@repo/icons/default/MoneyBanknoteHandshake";
import PartyConfetti from "@repo/icons/default/PartyConfetti";
import TimeLoop from "@repo/icons/default/TimeLoop";
import User1 from "@repo/icons/default/User1";
import UserGroup from "@repo/icons/default/UserGroup";
import { CpsBadge } from "corpus";

import { SettingsSectionReferralBanner } from "@/components/settings/settings-section-referral-banner";
import { BottomBarV2 } from "@/components/ui/layout/bottom-bar/bottom-bar-v2";
import { HeaderRoot, HeaderTitle } from "@/components/ui/layout/header/header";
import {
  MenuList,
  MenuListCard,
  type MenuListItem,
} from "@/components/ui/layout/menu/menu-list";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { useZendeskLogin } from "@/hooks/use-zendesk-login";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";
import { getAppInfo } from "@/lib/utils/app-info";

const SettingsPageQuery = graphql(/* GraphQL */ `
  query SettingsPageQuery {
    locus_membro {
      slug
      ativo
    }
  }
`);

const SettingsPage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const { loginWithZendesk } = useZendeskLogin();
  const { data: oneLivData } = useGraphQL(SettingsPageQuery, {});
  const actualVersion = useQuery({
    queryKey: ["appVersion"],
    queryFn: async () => {
      return await getAppInfo();
    },
  });

  const userHasAssist = user.pacotesServicos.some(
    (servicePackage) => servicePackage.grupoUpgrade === "Assist",
  );

  const userHasAssistLegacy = user.pacotesServicos.some(
    (servicePackage) =>
      servicePackage.codPacoteServicos === ServicePackageEnum.Legacy.valueOf(),
  );

  const { flags } = useRouteContext({ from: "__root__" });

  const showAssistMenuFF = useMemo(
    () => flags["membro-pode-contratar-pacote-servicos"],
    [flags],
  );

  const shouldDisplayChatBotButton = useMemo(
    () => flags["exibe-botoes-do-chatbot-zendesk-no-app"]?.settings ?? false,
    [flags],
  );

  const shouldDisplayBlog = useMemo(() => flags["mostra-blog-no-app"], [flags]);

  const regulatoryDocumentsFlagEnabled = useMemo(
    () => Boolean(flags["exibe-documentos-regulatorios-no-app"]),
    [flags],
  );

  const showRegulatoryDocuments =
    regulatoryDocumentsFlagEnabled && user.possuiAgendaAtiva;

  const showLivanceAssistMenu =
    showAssistMenuFF && (userHasAssist || userHasAssistLegacy);
  const showCommunity = flags["mostra-comunidade-no-app"];
  const showNewHome = useMemo(() => flags["exibe-nova-home"], [flags]);
  const showGoogleCalendar = useMemo(
    () => Boolean(flags["exibe-integracao-google-agenda"]),
    [flags],
  );

  const isOneLivProfileActive = useMemo(() => {
    if (!oneLivData?.locus_membro || oneLivData.locus_membro.length === 0) return false;
    return oneLivData.locus_membro[0]?.ativo ?? false;
  }, [oneLivData]);

  const scheduleMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Configurações da agenda",
      Icon: Adjustments,
      route: "/settings/schedule-options",
      show: true,
    },
    {
      name: "Agendamento recorrente",
      Icon: CalendarTime,
      route: "/recurring-appointments",
      show: Boolean(flags["exibir-agendamento-recorrente"]),
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
    },
    {
      name: "Período garantido",
      Icon: CalendarTime,
      route: "/settings/reserved-schedules",
      show: true,
    },
    {
      name: "Google Agenda",
      Icon: GoogleColored,
      route: "/settings/google-calendar",
      show: showGoogleCalendar,
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
    },
  ];

  const activeScheduleMenuItems = scheduleMenuItems.filter((item) => item.show);

  const servicesMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Livance Assist",
      Icon: GraphList,
      route: "/settings/assist",
      show: showLivanceAssistMenu,
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
      description:
        "Aumente sua conversão de agendamentos, fidelize pacientes e veja seu consultório crescer.",
    },
    {
      name: "Livance Pay",
      Icon: LivancePay,
      route: "/settings/payments",
      show: true,
    },
    {
      name: "Comunidade",
      Icon: UserGroup,
      route: "/settings/community",
      show: showCommunity,
    },
  ];

  const activeServicesMenuItems = servicesMenuItems.filter((item) => item.show);

  const socialMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Meu site profissional",
      Icon: GlobeCursor,
      route: "/settings/professional-site",
      show: true,
    },
    {
      name: "Blog",
      Icon: EditMessage,
      route: "/settings/blog",
      show: shouldDisplayBlog,
    },
    {
      name: "Perfil OneLiv",
      Icon: OnelivColored,
      route: "/oneliv/welcome",
      show: true,
      tag: isOneLivProfileActive ? <CpsBadge color="success">Ativo</CpsBadge> : undefined,
    },
    {
      name: "Eventos Livance",
      Icon: PartyConfetti,
      onClick: () => {
        void Browser.open({ url: "https://luma.com/Livance" });
      },
      show: true,
    },
  ];

  const activeSocialMenuItems = socialMenuItems.filter((item) => item.show);

  const accountMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Meu perfil",
      Icon: User1,
      route: "/settings/profile",
      show: true,
    },
    {
      name: "Indique e ganhe",
      Icon: MoneyBanknoteHandshake,
      route: "/settings/referral",
      show: true,
    },
    {
      name: "Histórico de uso",
      Icon: TimeLoop,
      route: "/settings/billing",
      show: true,
    },
  ];

  const activeAccountMenuItems = accountMenuItems.filter((item) => item.show);

  const supportMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Informações de unidades",
      Icon: Building,
      route: "/locations",
      show: true,
    },
    {
      name: "Documentos regulatórios",
      Icon: Folder,
      route: "/settings/regulatory-documents",
      show: showRegulatoryDocuments,
      tag: <CpsBadge color="helper">Novo</CpsBadge>,
    },
    {
      name: "Preciso de ajuda",
      Icon: BlurpIa,
      onClick: loginWithZendesk,
      show: shouldDisplayChatBotButton,
    },
    {
      name: "Central de ajuda",
      Icon: HelpCircle,
      externalRoute: "https://ajuda.livance.com.br/hc/pt-br",
      show: !shouldDisplayChatBotButton,
      target: "_blank",
    },
    {
      name: "Sair do aplicativo",
      Icon: Logout,
      route: "/auth/logout",
      // O beforeLoad faz o logout: o preload deslogaria o usuário.
      preload: false,
      show: true,
    },
  ];

  const activeSupportMenuItems = supportMenuItems.filter((item) => item.show);

  return (
    <>
      <HeaderRoot>
        <HeaderTitle title="Mais opções" />
      </HeaderRoot>
      <Page className="p-6 pb-[120px] space-y-6" sheet={showNewHome}>
        <SettingsSectionReferralBanner />
        <MenuListCard>
          <MenuList menuItems={activeServicesMenuItems} />
        </MenuListCard>
        <MenuListCard>
          <MenuList menuItems={activeScheduleMenuItems} />
        </MenuListCard>
        {activeSocialMenuItems.length > 0 ? (
          <MenuListCard>
            <MenuList menuItems={activeSocialMenuItems} />
          </MenuListCard>
        ) : null}
        <MenuListCard>
          <MenuList menuItems={activeAccountMenuItems} />
        </MenuListCard>
        <MenuListCard>
          <MenuList menuItems={activeSupportMenuItems} />
        </MenuListCard>
        {actualVersion.data ? (
          <p className="flex justify-center  text-xs text-slate-300">
            {actualVersion.data.version}
          </p>
        ) : null}
      </Page>
      <BottomBarV2 />
    </>
  );
};

export const Route = createFileRoute("/settings/")({
  component: SettingsPage,
});
